import React from 'react'
import ReactMarkdown from 'react-markdown'
import PropTypes from 'prop-types'

import styled, { css } from 'styled-components'
import { color, layout, space, typography } from 'styled-system'
import { themeGet } from '@styled-system/theme-get'

import { TEXT_SHOULD_FORWARD_PROP } from 'Config/shouldForwardProp'

const textSmallCss = ({ small }) =>
  small &&
  css`
    font-size: 14px;
  `

const textTitleCss = ({ title }) =>
  title &&
  css`
    font-size: 48px;
    font-weight: 500;
    letter-spacing: 1px;
    line-height: 1.42;

    @media screen and (max-width: ${themeGet('breakpoints.0')}) {
      font-size: 24px;
      letter-spacing: 0.5px;
      line-height: 1.33;
    }
  `

const textLightCss = ({ light }) =>
  light &&
  css`
    font-weight: 300;
  `

const textMediumCss = ({ medium }) =>
  medium &&
  css`
    font-weight: 500;
  `

const textBoldCss = ({ bold }) =>
  bold &&
  css`
    font-weight: 700;
  `

const textUppercaseCss = ({ uppercase }) =>
  uppercase &&
  css`
    text-transform: uppercase;
  `

const textUnderlineCss = ({ underline }) =>
  underline &&
  css`
    text-decoration: underline;
  `

const textLinkCss = ({ link }) =>
  link &&
  css`
    cursor: pointer;
    user-select: none;
    color: ${themeGet('colors.font.link')};
  `

const textSecondaryCss = ({ secondary }) =>
  secondary &&
  css`
    color: ${themeGet('colors.font.secondary')};
  `

const textWhiteCss = ({ white }) =>
  white &&
  css`
    color: ${themeGet('colors.white')};
  `

const textCenterCss = ({ center }) =>
  center &&
  css`
    text-align: center;
  `

const textJustifyCss = ({ justify }) =>
  justify &&
  css`
    text-align: justify;
  `

const textNoWrapCss = ({ nowrap }) =>
  nowrap &&
  css`
    white-space: nowrap;
  `

const shouldForwardProp = prop => {
  return ![...TEXT_SHOULD_FORWARD_PROP, 'deepBlue', 'legacyTitle'].includes(
    String(prop),
  )
}

const BaseText = styled.div.withConfig({ shouldForwardProp })`
  position: relative;
  z-index: 1;
  margin: 0;
  white-space: pre-wrap;
  font-size: 20px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.6;
  letter-spacing: 0.5px;
  color: ${themeGet('colors.font.primary')};

  > p:first-child {
    margin-top: 0;
  }

  ${textBoldCss}
  ${textCenterCss}
  ${textJustifyCss}
  ${textLightCss}
  ${textLinkCss}
  ${textMediumCss}
  ${textNoWrapCss}
  ${textSecondaryCss}
  ${textSmallCss}
  ${textTitleCss}
  ${textUnderlineCss}
  ${textUppercaseCss}
  ${textWhiteCss}

  ${({ textTransform }) => textTransform && `text-transform: ${textTransform};`}

  ${typography};
  ${space};
  ${color};
  ${layout};
`

const Text = ({ children, markdown = false, ...rest }) => {
  if (markdown) {
    return (
      <BaseText {...rest}>
        <ReactMarkdown
          components={{
            h1: 'h2',
          }}
        >
          {children}
        </ReactMarkdown>
      </BaseText>
    )
  }

  return <BaseText {...rest}>{children}</BaseText>
}

Text.propTypes = {
  bold: PropTypes.bool,
  center: PropTypes.bool,
  children: PropTypes.any,
  justify: PropTypes.bool,
  light: PropTypes.bool,
  link: PropTypes.bool,
  markdown: PropTypes.bool,
  medium: PropTypes.bool,
  nowrap: PropTypes.bool,
  secondary: PropTypes.bool,
  small: PropTypes.bool,
  title: PropTypes.bool,
  underline: PropTypes.bool,
  uppercase: PropTypes.bool,
  white: PropTypes.bool,
}

export default Text
